import { useEffect, useMemo, useRef } from 'react';
import { augmentCheckInSurvey } from '../customization/augmentCheckInSurvey';
import type {
  CheckInCustomizationContext,
  CheckInPrefill,
} from '../customization/types';
import { WITHINGS_PREFILL_SOURCE } from '../customization/withingsPrefill/withingsPrefill.config';
import { useCheckInSurveyStore } from './checkinSurvey.store';

type WebViewPayload = {
  surveyData?: string;
  lastInjectionCount?: number | null;
  checkInPrefill?: CheckInPrefill | null;
};

const readCheckInPrefill = (
  value: CheckInPrefill | null | undefined,
): CheckInPrefill | null => {
  if (!value || value.source !== WITHINGS_PREFILL_SOURCE) return null;
  if (!value.answers || typeof value.answers !== 'object') return null;
  return value;
};

export const useCheckInSurveyBootstrap = (): CheckInCustomizationContext => {
  const setCheckInSurveyFromString = useCheckInSurveyStore(
    (store) => store.setCheckInSurveyFromString,
  );

  const w = window as unknown as WebViewPayload;
  const lastInjectionCount =
    typeof w.lastInjectionCount === 'number' ? w.lastInjectionCount : null;
  const prefillKey = JSON.stringify(w.checkInPrefill ?? null);
  const checkInPrefill = useMemo(
    () => readCheckInPrefill(w.checkInPrefill),
    // prefillKey is the stable snapshot of the injected payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prefillKey],
  );

  const context = useMemo<CheckInCustomizationContext>(
    () => ({ lastInjectionCount, checkInPrefill }),
    [lastInjectionCount, checkInPrefill],
  );

  // Bootstrap runs once: re-loading on answer changes would reset in-progress survey state.
  const hasBootstrapped = useRef(false);

  useEffect(() => {
    if (hasBootstrapped.current || !w.surveyData) return;
    hasBootstrapped.current = true;
    const augmented = augmentCheckInSurvey(w.surveyData, context);
    setCheckInSurveyFromString(augmented);
  }, [setCheckInSurveyFromString, context, w.surveyData]);

  return context;
};
